import {getAssistantTextBlocks} from '@github-ui/ahp-relay/task-complete'
import {MessageKind} from '@microsoft/agent-host-protocol'
import type {ActiveTurn, ChatState, Turn} from '@microsoft/agent-host-protocol'

import type {ExportableConversation, ExportableMessage} from './types'

export interface AhpExportOptions {
  /** Overrides `state.title`; lets callers apply a product-specific default. */
  title?: string
  url?: string
  user?: string
}

function assistantText(turn: Turn | ActiveTurn): string {
  // Keep distinct markdown response parts as separate blocks; only streaming
  // deltas within one part share a part id, so distinct array entries are
  // distinct blocks (mirrors how the timeline renders responseParts). An
  // autopilot `task_complete` summary is included only when the timeline shows
  // it as the answer.
  return getAssistantTextBlocks(turn.responseParts).join('\n\n')
}

// Export mirrors the on-screen conversation: only the user's own messages plus the
// assistant's Markdown responses. Agent/tool/system-origin messages are internal
// (seeds, notifications) and reasoning/tool-call parts are omitted.
function appendTurn(messages: ExportableMessage[], turn: Turn | ActiveTurn): void {
  if (turn.message.origin.kind === MessageKind.User && turn.message.text) {
    messages.push({id: turn.id, role: 'user', content: turn.message.text})
  }

  const response = assistantText(turn)
  if (response) {
    messages.push({id: `${turn.id}-response`, role: 'assistant', content: response})
  }
}

export function fromAhpChatState(state: ChatState, options: AhpExportOptions = {}): ExportableConversation {
  const messages: ExportableMessage[] = []

  for (const turn of state.turns ?? []) {
    appendTurn(messages, turn)
  }
  // Include the in-progress turn so a mid-stream export matches the timeline.
  if (state.activeTurn) {
    appendTurn(messages, state.activeTurn)
  }

  return {
    title: options.title ?? state.title,
    ...(options.url ? {url: options.url} : {}),
    ...(options.user ? {participants: {user: options.user}} : {}),
    messages,
  }
}
